import { useEffect, useRef, useState } from "react";
import {
  CategoryItem,
  CategoryText,
  Container,
  FadeWrapper,
  FolderItem,
  InfoContainer,
  InfoText,
  InfoTitle,
  Relative,
  SearchFilter,
  StyledInput,
} from "./CollectionFilter.styles";
import { ReactComponent as FavoritesIcon } from "../../assets/icons/favorites.svg";
import { ReactComponent as PlaylistIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import { useSelector } from "react-redux";
import {
  selectCategories,
  selectExploreCollectionsKeyword,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { useNavigate, useSearchParams } from "react-router-dom";
import Dropdown from "../Dropdown/Dropdown";
import { ALLOWED_TAGS } from "../../utils/constants";
import LottieIcon from "../LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import searchAnimation from "../../assets/animations/searchToX.json";
import { ReactComponent as InfoSvg } from "../../assets/icons/info-circle.svg";
import ReactDOM from "react-dom";
import { useScrollFade } from "../../hooks/useScrollFade";
import { selectUser } from "../../store/slices/user/slice";

const LONG_PRESS_DURATION = 500;

const CATEGORY_WIDTHS = {
  Trending: 70,
  New: 44,
  "Top Rated": 79.5,
  "Most Favorited": 111,
  "VIP Creators": 121,
  Picture: 59,
  "4K": 44,
};

const CollectionFilter = ({
  onSearchChange,
  filter,
  loading,
  minHeight,
  searchValue,
  setFilter,
  hideFilter,
  hideSearch = false,
  disableSearch = false,
  onDisabledSearchClick,
  searchPlaceholderText,
  openCreateFolderModal,
  folders,
  showFolders,
  openDeleteFolder,
  showSelect,
  setSelectedDeleteFolder,
  setSelect,
  select,
  selectedCollections,
  offHorizontalPadding,
  hidePicturesCategory,
  onChangeKeyword,
  isProEnabled,
  hidePlaylist,
  isExtendModal,
  extendData,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInfo, setShowInfo] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(
    !!searchValue ?? false,
  );
  const user = useSelector(selectUser);
  const { data: categories } = useSelector(selectCategories);
  const loggedIn = useSelector(selectLoggedIn);
  const inputRef = useRef();
  const savedKeyword = useSelector(selectExploreCollectionsKeyword);
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);
  const infoIconRef = useRef(null);
  const [infoPos, setInfoPos] = useState({ top: 0, left: 0 });

  const [categoryBarRef, isCategoryFaded] = useScrollFade("horizontal", [
    categories,
  ]);

  useEffect(() => {
    if (disableSearch) {
      setIsSearchExpanded(false);
    }
  }, [disableSearch]);

  const handleDisabledSearchClick = (e) => {
    if (!disableSearch) return false;

    e?.preventDefault();
    e?.stopPropagation();
    inputRef.current?.blur();
    onDisabledSearchClick?.();
    return true;
  };

  const handleInfoClick = (e, openCategory) => {
    if (!openCategory) {
      e.stopPropagation();
      const rect = infoIconRef.current?.getBoundingClientRect();
      if (rect) {
        const tooltipWidth = window.innerWidth <= 342 ? 290 : 329;
        const left = Math.max(
          8,
          rect.left + window.scrollX - tooltipWidth + 18,
        );
        setInfoPos({
          top: rect.bottom + window.scrollY + 8,
          left,
        });
      }
      setShowInfo((prev) => !prev);
    }
  };

  useEffect(() => {
    if (!showInfo) return;
    const handleClickOutside = (e) => {
      if (infoIconRef.current && !infoIconRef.current.contains(e.target)) {
        setShowInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showInfo]);

  const startLongPress = (folder) => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setSelectedDeleteFolder(folder);
      openDeleteFolder(folder._id);
    }, LONG_PRESS_DURATION);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleFolderContextMenu = (e, folder) => {
    e.preventDefault();
    setSelectedDeleteFolder(folder);
    openDeleteFolder(folder._id);
  };

  const handleFolderClick = (folder) => {
    if (isLongPress.current) {
      isLongPress.current = false;
      return;
    }
    setFilter(folder._id);
  };

  return (
    <FadeWrapper $faded={isCategoryFaded}>
      <Container
        ref={categoryBarRef}
        $offHorizontalPadding={offHorizontalPadding}
        $minHeight={minHeight}
      >
        {loggedIn && showSelect && (
          <CategoryItem
            $width={select ? "fit-content" : 44}
            style={{
              padding: "0 10px",
              width: select ? "fit-content" : "44px",
            }}
            onClick={() => setSelect(!select)}
          >
            <LottieIcon
              animationData={checkboxAnimation}
              speed={2}
              trigger="toggle-reverse"
              isActive={select}
              onActiveChange={() => setSelect(!select)}
              onClick={() => setSelect(!select)}
              size={24}
            />
            {select && (
              <CategoryText>{selectedCollections?.length}/20</CategoryText>
            )}
          </CategoryItem>
        )}

        {!hideSearch && (
          <SearchFilter
            $expanded={isSearchExpanded}
            onMouseDown={handleDisabledSearchClick}
          >
            <LottieIcon
              animationData={searchAnimation}
              trigger="toggle-reverse"
              isActive={isSearchExpanded}
              strokeColor="#fff"
              speed={2}
              absolute
              onClick={() => {
                if (handleDisabledSearchClick()) return;

                if (isSearchExpanded) {
                  setIsSearchExpanded(false);
                  onSearchChange("");
                  inputRef.current?.blur();
                } else {
                  setIsSearchExpanded(true);
                  inputRef.current?.focus();
                }
              }}
            />
            <StyledInput
              ref={inputRef}
              value={searchValue}
              placeholder={searchPlaceholderText ?? "title, username or tags"}
              readOnly={disableSearch}
              onFocus={() => {
                if (handleDisabledSearchClick()) {
                  return;
                }

                setIsSearchExpanded(true);
              }}
              onBlur={() => {
                if (!searchValue) {
                  setTimeout(() => setIsSearchExpanded(false), 100);
                }
              }}
              onChange={(e) => {
                if (!disableSearch) {
                  onSearchChange(e.target.value);
                }
              }}
            />
          </SearchFilter>
        )}

        {loggedIn && showFolders && (
          <>
            <CategoryItem $width={44} onClick={openCreateFolderModal}>
              <PlusCircleIcon color={"#fff"} />
            </CategoryItem>
            <FolderItem
              $selected={filter === "ALL"}
              $width={44}
              onClick={() => setFilter("ALL")}
            >
              <CategoryText>All</CategoryText>
            </FolderItem>
            <FolderItem
              $selected={filter === "FAVORITES"}
              onClick={() => setFilter("FAVORITES")}
            >
              <CategoryText>Favorites</CategoryText>
            </FolderItem>
            {folders.map((folder, i) => (
              <FolderItem
                key={folder._id || i}
                $selected={filter === folder._id}
                onClick={() => handleFolderClick(folder)}
                onMouseDown={() => startLongPress(folder)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(folder)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                onContextMenu={(e) => handleFolderContextMenu(e, folder)}
              >
                <CategoryText>{folder.name}</CategoryText>
              </FolderItem>
            ))}
          </>
        )}

        {!hideFilter &&
          (loading && categories?.length === 0 ? (
            <>
              {/* Favorites - samo ako je loggedIn */}
              <CategoryItem $width={44} $animation />

              {/* Playlist */}
              {!hidePlaylist && <CategoryItem $width={44} $animation />}

              {/* Dropdown */}
              <CategoryItem $width={65.77} $animation />

              {/* Trending, New, Top Rated, Most Favorited, VIP Creators, 4K, Picture */}
              <CategoryItem $width={70} $animation />
              <CategoryItem $width={44} $animation />
              <CategoryItem $width={79.5} $animation />
              <CategoryItem $width={111} $animation />
              <CategoryItem $width={121} $animation />
              <CategoryItem $width={44} $animation />
              <CategoryItem $width={59} $animation />

              {/* Following */}
              <CategoryItem $width={74} $animation />
            </>
          ) : (
            <>
              {loggedIn && (
                <CategoryItem
                  $selected={filter === "FAVORITES"}
                  $width={44}
                  onClick={() => {
                    searchParams.delete("page");
                    setSearchParams(searchParams);
                    setFilter("FAVORITES");
                  }}
                >
                  <FavoritesIcon color={"#fff"} />
                </CategoryItem>
              )}

              {!hidePlaylist && (
                <CategoryItem
                  $width={44}
                  onClick={() => {
                    searchParams.delete("page");
                    setSearchParams(searchParams);
                    navigate("/playlist");
                  }}
                >
                  <PlaylistIcon />
                </CategoryItem>
              )}

              <Dropdown
                options={[{ label: "All", id: 0 }, ...ALLOWED_TAGS]}
                placeholder="All"
                defaultValue={savedKeyword}
                onChange={(value) => {
                  if (onChangeKeyword) onChangeKeyword(value);
                }}
                isProEnabled={isProEnabled}
              />

              {isExtendModal && extendData && (
                <CategoryItem
                  $selected={filter === "EXTEND_WITH"}
                  $width={92}
                  onClick={() => setFilter("EXTEND_WITH")}
                >
                  <CategoryText $selected={filter === "EXTEND_WITH"}>
                    Extend With
                  </CategoryText>
                </CategoryItem>
              )}

              {!!hidePicturesCategory &&
                !!categories &&
                categories
                  .filter((category) => category.name !== "Picture")
                  .map((category) => (
                    <CategoryItem
                      $selected={filter === category._id}
                      key={category._id}
                      $width={CATEGORY_WIDTHS[category.name] ?? 44}
                      onClick={() => {
                        searchParams.delete("page");
                        setSearchParams(searchParams);
                        setFilter(category._id);
                      }}
                    >
                      <CategoryText $selected={filter === category._id}>
                        {category.name}
                      </CategoryText>
                    </CategoryItem>
                  ))}

              {!!categories &&
                !hidePicturesCategory &&
                categories.map((category) => (
                  <CategoryItem
                    $selected={filter === category._id}
                    key={category._id}
                    $padding={category.name === "VIP Creators"}
                    $width={CATEGORY_WIDTHS[category.name] ?? 44}
                    onClick={() => {
                      searchParams.delete("page");
                      setSearchParams(searchParams);
                      setFilter(category._id);
                    }}
                  >
                    <CategoryText $selected={filter === category._id}>
                      {category.name}
                    </CategoryText>
                    {category.name === "VIP Creators" && (
                      <Relative>
                        <InfoSvg
                          ref={infoIconRef}
                          width={18}
                          height={18}
                          onClick={(e) =>
                            handleInfoClick(e, filter !== category._id)
                          }
                        />
                        {showInfo &&
                          ReactDOM.createPortal(
                            <InfoContainer
                              style={{
                                position: "absolute",
                                top: infoPos.top,
                                left: infoPos.left,
                              }}
                            >
                              <InfoTitle>
                                The VIP section is reserved exclusively for
                                templates of our highest-quality creators.
                              </InfoTitle>
                              <InfoText>To apply for VIP status:</InfoText>
                              <InfoText>
                                Submit 10 or more unique, high-quality templates
                                in the Train section. Once your templates start
                                gaining significant usage and you show up on the
                                leaderboard, an Apply for VIP button will appear
                                for you.
                              </InfoText>
                            </InfoContainer>,
                            document.body,
                          )}
                      </Relative>
                    )}
                  </CategoryItem>
                ))}

              {loggedIn && user?.numOfFollowing > 0 && (
                <CategoryItem
                  $selected={filter === "FOLLOWING"}
                  $width={74}
                  onClick={() => {
                    searchParams.delete("page");
                    setSearchParams(searchParams);
                    setFilter("FOLLOWING");
                  }}
                >
                  <CategoryText>
                    {/* <FollowIcon color={"#fff"} /> */}
                    Following
                  </CategoryText>
                </CategoryItem>
              )}
            </>
          ))}
      </Container>
    </FadeWrapper>
  );
};

export default CollectionFilter;
